// Shared helper for deciding which "vertical" (extrusion-direction) edges to draw when rendering
// a wireframe extrusion of a cross-section outline (used by both the three.js and SVG 3D column
// views). Drawing a vertical edge at every perimeter vertex produces a cluttered result for
// outlines with many points (e.g. a circular section approximated by a polygon) - most of those
// edges don't contribute to the perceived shape.
//
// Instead, a vertical edge at vertex i is kept only if the two side faces meeting there (the
// quads between vertices i-1..i and i..i+1) form a silhouette, in one of two senses - both exact
// sign comparisons, not a tunable threshold, so the result is the same regardless of how finely
// the outline is tessellated (a circle approximated by 8 points or by 200 gives exactly the same
// 2 lines):
//  - the true 3D silhouette: one face is front-facing and the other back-facing relative to the
//    fixed isometric view direction ("isSilhouette" below), or
//  - the "flattened" silhouette (fakeSilhouette/isFakeSilhouette): the same test but against the
//    view direction projected onto the plane perpendicular to the extrusion axis, i.e. as if
//    looking straight down the column. This is what catches a rectangular section's *visible*
//    near corner - the edge between two faces that both happen to face the 3D camera (so
//    isSilhouette alone misses it) but that are still a real, visually meaningful corner (e.g. a
//    box's front-right edge). Gated off for round sections by the caller (fakeSilhouette=false),
//    since a circle is already fully described by its 2 true-silhouette edges and doesn't have
//    any such corners to add.
// A vertex where neither test flips sign lies strictly between two faces the tessellation only
// needed for a smooth curve, not a real feature - dropped, however many segments approximate it.
//
// Both 3D views use a genuine (equal-angle) isometric projection, so the fixed view/eye direction
// is the cube diagonal (1, 1, 1) in whichever (x, y, z) space the geometry is expressed in before
// any non-uniform per-axis scaling is (further) applied for projection - see ThreeColumnView.tsx
// and SvgIsometricColumnView.tsx for how each view's points already live in such a space.
//
// IMPORTANT: bottomPoints/topPoints passed in here must already include any non-uniform
// scale3dX/scale3dYZ scaling (anisotropic scaling changes face normals non-uniformly, so
// running this on unscaled points and a scaled camera direction - or vice versa - gives wrong
// results whenever scale3dX != scale3dYZ). ThreeColumnView bakes the scale into toWorld() before
// calling this; SvgIsometricColumnView's point()/projectPoint() only apply scale at projection
// time, so it must scale its own copies of the points before calling this function.
import { isometric_3d } from "../ViewportStyles";

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

const subtract = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });

const cross = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});

export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;

const pointsEqual = (a: Vec3, b: Vec3): boolean => dot(subtract(a, b), subtract(a, b)) <= 1e-18;

const average = (...points: Vec3[]): Vec3 => ({
  x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
  y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
  z: points.reduce((sum, point) => sum + point.z, 0) / points.length,
});

const normalize = (a: Vec3): Vec3 => {
  const len = Math.sqrt(dot(a, a));
  return len > 1e-9 ? { x: a.x / len, y: a.y / len, z: a.z / len } : a;
};

/**
 * Target-to-camera direction ("towards the viewer") in ThreeColumnView's mapped world
 * coordinates - the camera sits at +x/+y/+z relative to its target, see the camera setup in
 * ThreeColumnView. A face normal with dot(normal, direction) > 0 therefore faces the viewer, and
 * dot(point, direction) grows the *closer* a point is to the camera (use it as a depth key:
 * smallest first = furthest first).
 */
export const ISOMETRIC_VIEW_DIRECTION: Vec3 = normalize({ x: 1, y: 1, z: 1 });

/**
 * Target-to-camera direction in SvgIsometricColumnView's (height x - positive *down*,
 * cross-section y, cross-section z) coordinates, i.e. the null direction of isoProject
 *   screenH = (y - z) cos(30deg), screenV = x + (y + z) sin(30deg)
 * oriented towards the viewer: moving along (-1, 1, 1) leaves both screenH and screenV unchanged
 * (screenH: 1 - 1 = 0, screenV: -1 + (1 + 1)/2 = 0) while going up and towards the front of the
 * scene. Same sign convention as ISOMETRIC_VIEW_DIRECTION above.
 *
 * NOTE: the x component only matters for depth sorting - the extrusion's side-face normals are
 * always perpendicular to the extrusion (height) axis, so silhouette/back-face classification is
 * unaffected by it.
 */
export const SVG_ISOMETRIC_VIEW_DIRECTION: Vec3 = normalize({ x: -1, y: 1, z: 1 });

/**
 * Light direction for the "shade faces" mode, in ng space (x = column axis, positive up; y, z the
 * cross-section axes), from the light yaw/pitch settings: yaw turns about the column axis from +y
 * towards +z, pitch lifts the light from the cross-section plane towards +x. The isometric camera
 * sits at yaw 45deg, pitch 35.26deg.
 */
export const ngLightDirection = (yawDegrees: number, pitchDegrees: number): Vec3 => {
  const yaw = (yawDegrees * Math.PI) / 180;
  const pitch = (pitchDegrees * Math.PI) / 180;
  return { x: Math.sin(pitch), y: Math.cos(pitch) * Math.cos(yaw), z: Math.cos(pitch) * Math.sin(yaw) };
};

/** An ng-space direction in SvgIsometricColumnView's column coordinates (height x positive down). */
export const svgFromNgDirection = (v: Vec3): Vec3 => ({ x: -v.x, y: v.y, z: v.z });

/** An ng-space direction in ThreeColumnView's world coordinates (world y = ng x). */
export const threeFromNgDirection = (v: Vec3): Vec3 => ({ x: v.y, y: v.x, z: v.z });

/** Flat (non-shaded) support face color, used when "draw faces" is on but "shade faces" is off. */
export const FACE_SIDE_COLOR = isometric_3d.colors.supportFace;

/** Light/dark color pair blended per-face by shadeFaceColor() when "shade faces" is on. */
const FACE_SHADE_LIGHT_COLOR = isometric_3d.colors.supportShadeLight;
const FACE_SHADE_DARK_COLOR = isometric_3d.colors.supportShadeDark;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

export const shadeFactor = (normal: Vec3, lightDirection: Vec3): number =>
  clamp01(dot(normalize(normal), normalize(lightDirection)));

const parseRgb = (color: string): [number, number, number] => {
  const rgb = color.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  const value = parseInt(color.replace("#", ""), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const toLinear = (channel: number): number => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const fromLinear = (c: number): number =>
  255 * clamp01(c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

type Lab = [number, number, number];

const rgbToOklab = ([r8, g8, b8]: [number, number, number]): Lab => {
  const [r, g, b] = [toLinear(r8), toLinear(g8), toLinear(b8)];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
};

const oklabToHex = ([L, a, b]: Lab): string => {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const rgb = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
  return "#" + rgb.map((c) => Math.round(fromLinear(c)).toString(16).padStart(2, "0")).join("");
};

/**
 * Blends two opaque colors ("#rrggbb" or "rgb(...)") in oklab, like CSS `color-mix(in oklab, ...)`;
 * t=0 -> colorA, t=1 -> colorB (clamped to [0, 1]). Returns "#rrggbb".
 */
export const mixOklab = (colorA: string, colorB: string, t: number): string => {
  const a = rgbToOklab(parseRgb(colorA));
  const b = rgbToOklab(parseRgb(colorB));
  const k = clamp01(t);
  return oklabToHex([a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k]);
};

/**
 * Blends FACE_SHADE_DARK_COLOR/FACE_SHADE_LIGHT_COLOR using a simple "normal dot lighting" term: a
 * face pointing straight at the light is fully lit, one pointing straight away is fully dark, and
 * everything in between is an oklab blend - not physically accurate, just a cheap stand-in for
 * "realistic" shading.
 */
export const shadeFaceColor = (normal: Vec3, lightDirection: Vec3): string => {
  const t = shadeFactor(normal, lightDirection);
  return mixOklab(FACE_SHADE_DARK_COLOR, FACE_SHADE_LIGHT_COLOR, t);
};

/**
 * The extrusion's top-cap outward normal. The two loops passed to
 * selectExtrusionEdges/getExtrusionFaceNormals are just the same 2D outline translated along the
 * extrusion axis, so any bottom->top vertex vector already gives that direction - no separate
 * per-view "up" constant is needed.
 */
export const getExtrusionCapNormal = (bottomPoints: Vec3[], topPoints: Vec3[]): Vec3 => {
  if (bottomPoints.length === 0 || topPoints.length === 0) return { x: 0, y: 0, z: 0 };
  return normalize(subtract(topPoints[0], bottomPoints[0]));
};

/**
 * Returns, for each vertex of a closed loop (matching pairs of bottomPoints/topPoints), whether
 * the vertical edge connecting bottomPoints[i]-topPoints[i] should be drawn, along with a
 * human-readable explanation of the decision (silhouette/fake-silhouette dot products) that
 * callers can surface as a debug tooltip/label on that edge (see debugExtrusion3d).
 */
interface ExtrusionEdgeDecision {
  keep: boolean;
  debugInfo: string;
}

interface ExtrusionFaceNormal {
  fromIndex: number;
  toIndex: number;
  center: Vec3;
  normal: Vec3;
  viewDot: number;
  isEdgeOn: boolean;
}

/**
 * Selects all face normals whose midpoint along the ordered outline lies inside the configured
 * percentage range. Radius is the total range width: center 50 and radius 50 selects 25..75%.
 */
export const selectExtrusionFaceNormalsForDebug = (
  faces: ExtrusionFaceNormal[],
  centerPercent: number,
  radiusPercent: number,
): ExtrusionFaceNormal[] => {
  const center = Math.min(100, Math.max(0, centerPercent));
  const radius = Math.min(100, Math.max(0, radiusPercent));
  const start = Math.max(0, center - radius / 2);
  const end = Math.min(100, center + radius / 2);

  return faces.filter((_, index) => {
    const midpointPercent = ((index + 0.5) / faces.length) * 100;
    return midpointPercent >= start && midpointPercent <= end;
  });
};

export const getExtrusionFaceNormals = (
  bottomPoints: Vec3[],
  topPoints: Vec3[],
  viewDirection: Vec3,
): ExtrusionFaceNormal[] => {
  const pointCount = bottomPoints.length;
  if (topPoints.length !== pointCount) {
    throw new Error(
      `getExtrusionFaceNormals requires matching bottom/top point counts (${pointCount} !== ${topPoints.length})`,
    );
  }
  if (pointCount < 2) return [];

  const hasDuplicateClosingPoint =
    pointsEqual(bottomPoints[0], bottomPoints[pointCount - 1]) && pointsEqual(topPoints[0], topPoints[pointCount - 1]);
  const loopLength = hasDuplicateClosingPoint ? pointCount - 1 : pointCount;
  const normalizedViewDirection = normalize(viewDirection);
  const faces: ExtrusionFaceNormal[] = [];

  for (let fromIndex = 0; fromIndex < loopLength; fromIndex++) {
    const toIndex = (fromIndex + 1) % loopLength;
    if (
      pointsEqual(bottomPoints[fromIndex], bottomPoints[toIndex]) &&
      pointsEqual(topPoints[fromIndex], topPoints[toIndex])
    ) {
      continue;
    }

    const edge = subtract(bottomPoints[toIndex], bottomPoints[fromIndex]);
    const vertical = subtract(topPoints[fromIndex], bottomPoints[fromIndex]);
    const normal = normalize(cross(vertical, edge));
    const viewDot = dot(normal, normalizedViewDirection);
    faces.push({
      fromIndex,
      toIndex,
      center: average(bottomPoints[fromIndex], bottomPoints[toIndex], topPoints[toIndex], topPoints[fromIndex]),
      normal,
      viewDot,
      isEdgeOn: Math.sign(viewDot) === 0,
    });
  }

  return faces;
};

export const selectExtrusionEdges = (
  bottomPoints: Vec3[],
  topPoints: Vec3[],
  viewDirection: Vec3,
  fakeSilhouette: boolean = false,
): ExtrusionEdgeDecision[] => {
  const pointCount = bottomPoints.length;
  if (topPoints.length !== pointCount) {
    throw new Error(
      `selectExtrusionEdges requires matching bottom/top point counts (${pointCount} !== ${topPoints.length})`,
    );
  }

  const hasDuplicateClosingPoint =
    pointCount > 1 &&
    pointsEqual(bottomPoints[0], bottomPoints[pointCount - 1]) &&
    pointsEqual(topPoints[0], topPoints[pointCount - 1]);
  const loopLength = hasDuplicateClosingPoint ? pointCount - 1 : pointCount;

  if (loopLength < 3) {
    return bottomPoints.map((_, i) => {
      const keep = !hasDuplicateClosingPoint || i !== pointCount - 1;
      return { keep, debugInfo: `index: ${i}, keep: ${keep} (fewer than 3 points)` };
    });
  }

  const normalizedViewDirection = normalize(viewDirection);

  const sameExtrusionVertex = (a: number, b: number) =>
    pointsEqual(bottomPoints[a], bottomPoints[b]) && pointsEqual(topPoints[a], topPoints[b]);

  const findDistinctNeighbour = (index: number, step: -1 | 1): number | null => {
    for (let distance = 1; distance < loopLength; distance++) {
      const candidate = (index + step * distance + loopLength) % loopLength;
      if (!sameExtrusionVertex(index, candidate)) {
        return candidate;
      }
    }
    return null;
  };

  return bottomPoints.map((_, index) => {
    if (hasDuplicateClosingPoint && index === pointCount - 1) {
      return { keep: false, debugInfo: `index: ${index}, skipped (duplicates loop start vertex)` };
    }
    const previousIndex = (index - 1 + loopLength) % loopLength;
    if (index !== 0 && sameExtrusionVertex(index, previousIndex)) {
      return { keep: false, debugInfo: `index: ${index}, skipped (duplicates previous vertex)` };
    }
    const prev = findDistinctNeighbour(index, -1);
    const next = findDistinctNeighbour(index, 1);
    if (prev === null || next === null) {
      return { keep: false, debugInfo: `index: ${index}, skipped (no distinct adjacent vertices)` };
    }

    const vertical = subtract(topPoints[index], bottomPoints[index]);
    const incomingEdge = subtract(bottomPoints[index], bottomPoints[prev]);
    const outgoingEdge = subtract(bottomPoints[next], bottomPoints[index]);
    const normalPrev = normalize(cross(vertical, incomingEdge));
    const normalCurr = normalize(cross(vertical, outgoingEdge));
    const prevDot = dot(normalPrev, normalizedViewDirection);
    const currDot = dot(normalCurr, normalizedViewDirection);
    const isEdgeOn = Math.sign(prevDot) === 0 || Math.sign(currDot) === 0;
    const isSilhouette = Math.sign(prevDot) !== Math.sign(currDot);
    const verticalDirection = normalize(vertical);
    const viewVerticalComponent = dot(normalizedViewDirection, verticalDirection);
    const planarViewDirection = normalize({
      x: normalizedViewDirection.x - verticalDirection.x * viewVerticalComponent,
      y: normalizedViewDirection.y - verticalDirection.y * viewVerticalComponent,
      z: normalizedViewDirection.z - verticalDirection.z * viewVerticalComponent,
    });
    const prevSin = dot(cross(normalPrev, planarViewDirection), verticalDirection);
    const currSin = dot(cross(normalCurr, planarViewDirection), verticalDirection);
    const isFakeSilhouette = fakeSilhouette && Math.sign(prevSin) !== Math.sign(currSin);
    const debugInfo =
      `index: ${index} (${prev} <- ${index} -> ${next}), ` +
      `isSilhouette: ${isSilhouette}(${prevDot.toFixed(3)} | ${currDot.toFixed(3)}, ` +
      `edgeOn: ${isEdgeOn}), ` +
      `isFakeSilhouette: ${isFakeSilhouette}(${prevSin.toFixed(3)} | ${currSin.toFixed(3)})`;
    return { keep: isSilhouette || isFakeSilhouette, debugInfo };
  });
};
